import React, { useCallback, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import type { StylesConfig, SingleValue, GroupBase } from 'react-select';
import type { SelectOption } from './SearchableSelect';

interface AsyncSearchableSelectProps {
  label?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  loadOptions: (inputValue: string) => Promise<SelectOption[]>;
  placeholder?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  error?: string;
  variant?: 'default' | 'glass';
  className?: string;
  cacheOptions?: boolean;
  defaultOptions?: SelectOption[] | boolean;
  loadingMessage?: string;
  noOptionsMessage?: string;
  minLength?: number;
  /** Preselected option used to render the label when `value` is set but not yet in loaded options (e.g. edit forms). */
  initialOption?: SelectOption | null;
}

const getCustomStyles = (
  hasError: boolean,
  variant: 'default' | 'glass'
): StylesConfig<SelectOption, false, GroupBase<SelectOption>> => ({
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    backgroundColor: variant === 'glass' ? 'rgba(255, 255, 255, 0.1)' : 'var(--color-surface, #ffffff)',
    borderColor: hasError
      ? 'rgb(252, 165, 165)'
      : state.isFocused
        ? 'rgb(59, 130, 246)'
        : 'var(--color-border, #e5e7eb)',
    borderRadius: '0.5rem',
    boxShadow: state.isFocused
      ? hasError
        ? '0 0 0 2px rgba(239, 68, 68, 0.2)'
        : '0 0 0 2px rgba(59, 130, 246, 0.2)'
      : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '&:hover': {
      borderColor: hasError ? 'rgb(252, 165, 165)' : 'rgb(156, 163, 175)',
    },
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.6 : 1,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '2px 12px',
  }),
  input: (base) => ({
    ...base,
    color: 'var(--color-text, #111827)',
    margin: 0,
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--color-text-tertiary, #9ca3af)',
    fontSize: '0.875rem',
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--color-text, #111827)',
    fontSize: '0.875rem',
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--color-surface, #ffffff)',
    border: '1px solid var(--color-border, #e5e7eb)',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 9999,
    overflow: 'hidden',
  }),
  menuList: (base) => ({
    ...base,
    padding: '4px',
    maxHeight: '200px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'rgb(59, 130, 246)'
      : state.isFocused
        ? 'rgb(243, 244, 246)'
        : 'transparent',
    color: state.isSelected ? '#ffffff' : 'var(--color-text, #111827)',
    fontSize: '0.875rem',
    padding: '10px 12px',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: state.isSelected ? 'rgb(59, 130, 246)' : 'rgb(229, 231, 235)',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: 'var(--color-text-tertiary, #9ca3af)',
    fontSize: '0.875rem',
    padding: '10px 12px',
  }),
  loadingMessage: (base) => ({
    ...base,
    color: 'var(--color-text-tertiary, #9ca3af)',
    fontSize: '0.875rem',
    padding: '10px 12px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: 'var(--color-text-tertiary, #9ca3af)',
    padding: '8px',
    transition: 'transform 0.2s',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : undefined,
    '&:hover': {
      color: 'var(--color-text-secondary, #6b7280)',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'var(--color-text-tertiary, #9ca3af)',
    padding: '8px',
    '&:hover': {
      color: 'rgb(239, 68, 68)',
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999,
  }),
});

export const AsyncSearchableSelect: React.FC<AsyncSearchableSelectProps> = ({
  label,
  value,
  onChange,
  loadOptions,
  placeholder = 'Seleccionar...',
  isClearable = false,
  isDisabled = false,
  error,
  variant = 'default',
  className = '',
  cacheOptions = true,
  defaultOptions,
  loadingMessage = 'Cargando...',
  noOptionsMessage = 'Sin opciones',
  minLength = 0,
  initialOption = null,
}) => {
  const customStyles = getCustomStyles(!!error, variant);
  const selectedOptionRef = useRef<SelectOption | null>(initialOption);

  const handleLoadOptions = useCallback(async (inputValue: string) => {
    if (inputValue.length < minLength) {
      return [];
    }
    return await loadOptions(inputValue);
  }, [loadOptions, minLength]);

  const handleChange = (newValue: SingleValue<SelectOption>) => {
    selectedOptionRef.current = newValue;
    onChange(newValue ? newValue.value : null);
  };

  // Find the selected option from default options if provided
  const getSelectedValue = useCallback((): SelectOption | null => {
    if (!value) return null;
    
    // If we have a cached selected option, use it
    if (selectedOptionRef.current?.value === value) {
      return selectedOptionRef.current;
    }

    // If initialOption matches value, use it (e.g. edit forms where the value
    // is set programmatically but its option has not been loaded yet)
    if (initialOption && initialOption.value === value) {
      selectedOptionRef.current = initialOption;
      return initialOption;
    }

    // If defaultOptions is an array, try to find the value there
    if (Array.isArray(defaultOptions)) {
      const found = defaultOptions.find(opt => opt.value === value);
      if (found) {
        selectedOptionRef.current = found;
        return found;
      }
    }

    // Return the cached option if it exists
    return selectedOptionRef.current;
  }, [value, defaultOptions, initialOption]);

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-theme-secondary mb-1.5">
          {label}
        </label>
      )}
      <AsyncSelect<SelectOption, false, GroupBase<SelectOption>>
        cacheOptions={cacheOptions}
        defaultOptions={defaultOptions}
        loadOptions={handleLoadOptions}
        value={getSelectedValue()}
        onChange={handleChange}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        styles={customStyles}
        loadingMessage={() => loadingMessage}
        noOptionsMessage={() => noOptionsMessage}
        menuPlacement="auto"
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        classNamePrefix="react-select"
      />
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default AsyncSearchableSelect;
